// /xr-check — on-device WebXR capability probe.
//
// Documentation cannot answer the two questions the geo quest actually depends
// on, because they are about what a specific phone does at runtime:
//   1. Does `local-floor` give a real 6-DoF, metric, floor-at-y=0 pose?
//   2. Do `deviceorientationabsolute` and `watchPosition` KEEP DELIVERING while
//      an immersive-ar session is presenting? If Chrome suspends DOM sensor
//      events during an immersive session, the whole GPS+compass alignment
//      (src/placement/*) is dead on WebXR and Android must stay on 8th Wall.
//
// So this page runs the REAL sensor modules (startHeading / startGps, unmodified)
// inside a real immersive-ar session and counts what arrives. It deliberately
// opens its own XRSession rather than going through src/engine/engine.ts, because
// its job is to measure the platform — including features the app does not use.
import {useCallback, useEffect, useRef, useState} from 'react'
import * as THREE from 'three'
import {getEngineChoice, type EngineChoice} from '../engine/engine'
import {requestedRefSpace} from '../engine/webxrEngine'
import {startGps, type GpsTracker} from '../placement/gps'
import {startHeading, type HeadingTracker} from '../placement/heading'

type Phase = 'idle' | 'running' | 'ended' | 'error'

interface Live {
  frames: number
  poseOk: boolean
  emulated: boolean
  camX: number
  camY: number
  camZ: number
  yawDeg: number
  selects: number
  headingCount: number
  headingDeg: number | null
  headingSpread: number | null
  headingSource: string | null
  gpsCount: number
  gpsAcc: number | null
  gpsError: string | null
  thetaDeg: number | null
}

const EMPTY: Live = {
  frames: 0,
  poseOk: false,
  emulated: false,
  camX: 0,
  camY: 0,
  camZ: 0,
  yawDeg: 0,
  selects: 0,
  headingCount: 0,
  headingDeg: null,
  headingSpread: null,
  headingSource: null,
  gpsCount: 0,
  gpsAcc: null,
  gpsError: null,
  thetaDeg: null,
}

export function XrCheck() {
  const [phase, setPhase] = useState<Phase>('idle')
  const [error, setError] = useState('')
  const [statics, setStatics] = useState<Record<string, string>>({})
  const [choice, setChoice] = useState<EngineChoice | null>(null)
  const [features, setFeatures] = useState<string[]>([])
  const [overlayType, setOverlayType] = useState<string>('—')
  const [live, setLive] = useState<Live>(EMPTY)
  /** Highest heading/gps counts seen DURING the session — the actual verdict. */
  const [peak, setPeak] = useState({heading: 0, gps: 0, camY: 0})

  const rootRef = useRef<HTMLDivElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const liveRef = useRef<Live>({...EMPTY})
  const cleanupRef = useRef<(() => void) | null>(null)

  // Static capability report — no session, no permissions.
  useEffect(() => {
    const xr = navigator.xr
    const base: Record<string, string> = {
      'user agent': navigator.userAgent,
      'secure context': String(window.isSecureContext),
      'navigator.xr': xr ? 'present' : 'ABSENT',
      geolocation: 'geolocation' in navigator ? 'present' : 'ABSENT',
      DeviceOrientationEvent: 'DeviceOrientationEvent' in window ? 'present' : 'ABSENT',
      ondeviceorientationabsolute:
        'ondeviceorientationabsolute' in window ? 'present' : 'ABSENT (iOS-style compass)',
      'devicePixelRatio': String(window.devicePixelRatio),
      'reference space requested': requestedRefSpace(),
    }
    setStatics(base)

    let cancelled = false
    void (async () => {
      const extra: Record<string, string> = {}
      if (xr) {
        for (const mode of ['immersive-ar', 'immersive-vr'] as const) {
          try {
            extra[`isSessionSupported('${mode}')`] = String(await xr.isSessionSupported(mode))
          } catch (err) {
            extra[`isSessionSupported('${mode}')`] = `threw: ${String(err)}`
          }
        }
      }
      const c = await getEngineChoice()
      if (cancelled) return
      setChoice(c)
      setStatics((prev) => ({...prev, ...extra}))
    })()
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => () => cleanupRef.current?.(), [])

  const run = useCallback(async () => {
    if (!navigator.xr || !canvasRef.current || !rootRef.current) return
    setError('')
    liveRef.current = {...EMPTY}
    setLive({...EMPTY})
    setPeak({heading: 0, gps: 0, camY: 0})

    const refSpace = requestedRefSpace()
    let session: XRSession
    try {
      session = await navigator.xr.requestSession('immersive-ar', {
        requiredFeatures: [refSpace],
        // Probe more than the app uses, so the report says what this device could
        // support if we ever wanted hit-test/anchors/light-estimation.
        optionalFeatures: ['dom-overlay', 'hit-test', 'anchors', 'light-estimation'],
        domOverlay: {root: rootRef.current},
      })
    } catch (err) {
      setError(`requestSession failed: ${err instanceof Error ? err.message : String(err)}`)
      setPhase('error')
      return
    }

    setPhase('running')
    const enabled = (session as unknown as {enabledFeatures?: string[]}).enabledFeatures
    setFeatures(enabled ? [...enabled] : ['(enabledFeatures not reported)'])
    setOverlayType(session.domOverlayState?.type ?? 'none')

    const renderer = new THREE.WebGLRenderer({canvas: canvasRef.current, alpha: true, antialias: true})
    renderer.setClearAlpha(0)
    renderer.outputColorSpace = THREE.SRGBColorSpace
    renderer.xr.enabled = true
    renderer.xr.setReferenceSpaceType(refSpace)

    const scene = new THREE.Scene()
    const camera = new THREE.PerspectiveCamera(60, 1, 0.05, 1000)
    scene.add(new THREE.HemisphereLight(0xffffff, 0x334455, 1.4))

    // A grid ON the floor plane and a 0.4 m cube 2 m ahead: if the grid sits on
    // the real floor and the cube looks knee-high at 2 paces, local-floor is
    // giving a true metric, floor-referenced pose.
    const grid = new THREE.GridHelper(10, 20, 0x22d3ee, 0x155e75)
    scene.add(grid)
    const cube = new THREE.Mesh(
      new THREE.BoxGeometry(0.4, 0.4, 0.4),
      new THREE.MeshStandardMaterial({color: 0x22d3ee}),
    )
    cube.position.set(0, 0.2, -2)
    scene.add(cube)

    // The REAL sensor modules, unmodified. This is the crux measurement.
    const heading: HeadingTracker = startHeading()
    const gps: GpsTracker = startGps()
    let gpsCount = 0
    const gpsTick = setInterval(() => {
      if (gps.last) gpsCount = gpsCount + 1
    }, 1000)

    const onSelect = () => {
      liveRef.current.selects += 1
    }
    session.addEventListener('select', onSelect)

    try {
      await renderer.xr.setSession(session)
    } catch (err) {
      setError(`setSession failed: ${err instanceof Error ? err.message : String(err)}`)
      setPhase('error')
      return
    }
    rootRef.current.classList.add('xr-presenting')

    const forward = new THREE.Vector3()
    renderer.setAnimationLoop((_t, frame) => {
      const refSpace = renderer.xr.getReferenceSpace()
      const l = liveRef.current
      l.frames += 1
      if (frame && refSpace) {
        const pose = frame.getViewerPose(refSpace)
        l.poseOk = !!pose
        l.emulated = pose?.emulatedPosition ?? false
      }
      renderer.xr.updateCamera(camera)
      camera.getWorldDirection(forward)
      const alphaF = Math.atan2(forward.x, forward.z)
      l.camX = camera.position.x
      l.camY = camera.position.y
      l.camZ = camera.position.z
      l.yawDeg = ((alphaF * 180) / Math.PI + 360) % 360
      l.headingCount = heading.sampleCount
      l.headingDeg = heading.meanDeg
      l.headingSpread = heading.spreadDeg
      l.headingSource = heading.current?.source ?? null
      l.gpsCount = gpsCount
      l.gpsAcc = gps.last?.accuracy ?? null
      l.gpsError = gps.error
      // The alignment yaw the geo provider would apply, θ = αf + h − π.
      l.thetaDeg =
        heading.meanDeg === null
          ? null
          : ((((alphaF + (heading.meanDeg * Math.PI) / 180 - Math.PI) * 180) / Math.PI) + 360) % 360
      renderer.render(scene, camera)
    })

    const uiTimer = setInterval(() => {
      const l = liveRef.current
      setLive({...l})
      setPeak((p) => ({
        heading: Math.max(p.heading, l.headingCount),
        gps: Math.max(p.gps, l.gpsCount),
        camY: Math.max(p.camY, l.camY),
      }))
    }, 200)

    const cleanup = () => {
      clearInterval(uiTimer)
      clearInterval(gpsTick)
      renderer.setAnimationLoop(null)
      session.removeEventListener('select', onSelect)
      heading.stop()
      gps.stop()
      rootRef.current?.classList.remove('xr-presenting')
      renderer.dispose()
      cleanupRef.current = null
    }
    cleanupRef.current = () => {
      cleanup()
      void session.end().catch(() => {})
    }
    session.addEventListener('end', () => {
      cleanup()
      setLive({...liveRef.current})
      setPhase('ended')
    })
  }, [])

  const report = buildReport({statics, choice, features, overlayType, live, peak, phase})

  return (
    <div className="share-view" ref={rootRef}>
      <canvas ref={canvasRef} className="ar-canvas" />

      {phase !== 'running' && (
        <div className="landing">
          <div className="landing-body">
            <p className="eyebrow">Diagnostics</p>
            <h1>WebXR check</h1>
            <p className="dim">
              Probes native <code>immersive-ar</code> on this device, then runs the app's real
              compass + GPS modules <strong>inside</strong> the AR session to prove they keep
              delivering. Point the phone at the floor: the grid should sit on it.
            </p>
            {choice && (
              <div className="notice">
                Engine this device would use: <strong>{choice.kind}</strong>
                <br />
                <span className="dim">{choice.reason}</span>
              </div>
            )}
            {error && <div className="notice">{error}</div>}
            {phase === 'ended' && <Verdict live={live} peak={peak} features={features} />}
            <button className="btn primary" onClick={run}>
              {phase === 'idle' ? 'Start WebXR test' : 'Run again'}
            </button>
            <button
              className="btn ghost"
              onClick={() => void navigator.clipboard?.writeText(report)}
            >
              Copy report
            </button>
            <pre className="xr-report">{report}</pre>
          </div>
        </div>
      )}

      {phase === 'running' && (
        <div className="hud">
          <div className="hud-top">
            <span className="pill">WebXR live</span>
            <button className="pill btn-pill" onClick={() => cleanupRef.current?.()}>
              End
            </button>
          </div>
          <div className="hud-bottom">
            <pre className="xr-report">{liveBlock(live, features, overlayType)}</pre>
          </div>
        </div>
      )}
    </div>
  )
}

function Verdict({
  live,
  peak,
  features,
}: {
  live: Live
  peak: {heading: number; gps: number; camY: number}
  features: string[]
}) {
  // NOT a pass/fail: on Chrome Android `local-floor` is a hardcoded 1.2 m below
  // the session-start pose, so a "plausible" camera height proves nothing — it
  // is 1.2 m by construction. Only the operator's eyes can judge whether the
  // grid landed on the real floor, so this row reports and defers.
  const checks: Array<[string, boolean, string]> = [
    ['6-DoF pose (not emulated)', live.poseOk && !live.emulated, 'ARCore is tracking in 6 DoF'],
    ['dom-overlay granted', features.includes('dom-overlay'), 'the HUD can render over AR'],
    ['compass fires IN session', peak.heading > 0, `${peak.heading} heading samples`],
    ['GPS fires IN session', peak.gps > 0, `${peak.gps} fix ticks`],
  ]
  const allOk = checks.every(([, ok]) => ok)
  return (
    <div className="notice">
      <strong>{allOk ? 'Viable: the WebXR geo path works on this device.' : 'Problems found:'}</strong>
      <ul style={{margin: '8px 0 0', paddingLeft: 18}}>
        {checks.map(([label, ok, detail]) => (
          <li key={label}>
            {ok ? '✓' : '✗'} {label} — <span className="dim">{detail}</span>
          </li>
        ))}
        <li>
          ? floor — <span className="dim">
            peak camera Y {peak.camY.toFixed(2)} m ({requestedRefSpace()}). Chrome fakes
            local-floor at a fixed 1.2 m, so judge this by eye: did the grid sit on the
            real floor?
          </span>
        </li>
      </ul>
    </div>
  )
}

function liveBlock(live: Live, features: string[], overlayType: string): string {
  return [
    `frames        ${live.frames}   selects ${live.selects}`,
    `pose          ${live.poseOk ? 'ok' : 'NULL'}${live.emulated ? ' (EMULATED / 3-DoF)' : ' 6-DoF'}`,
    `camera        x=${live.camX.toFixed(2)} y=${live.camY.toFixed(2)} z=${live.camZ.toFixed(2)}`,
    `yaw αf        ${live.yawDeg.toFixed(1)}°`,
    `heading       ${live.headingDeg?.toFixed(1) ?? '—'}° (${live.headingSource ?? 'no source'}, n=${live.headingCount}, spread ${live.headingSpread?.toFixed(1) ?? '—'}°)`,
    `θ = αf+h−π    ${live.thetaDeg?.toFixed(1) ?? '—'}°`,
    `gps           ±${live.gpsAcc?.toFixed(0) ?? '—'} m  ticks=${live.gpsCount}${live.gpsError ? `  ⚠ ${live.gpsError}` : ''}`,
    `overlay       ${overlayType}`,
    `features      ${features.join(', ')}`,
  ].join('\n')
}

function buildReport(args: {
  statics: Record<string, string>
  choice: EngineChoice | null
  features: string[]
  overlayType: string
  live: Live
  peak: {heading: number; gps: number; camY: number}
  phase: Phase
}): string {
  const lines = ['── static ──']
  for (const [k, v] of Object.entries(args.statics)) lines.push(`${k}: ${v}`)
  if (args.choice) lines.push(`engine choice: ${args.choice.kind} (${args.choice.reason})`)
  if (args.phase === 'ended' || args.phase === 'running') {
    lines.push('', '── session ──')
    lines.push(`enabledFeatures: ${args.features.join(', ')}`)
    lines.push(`domOverlayState.type: ${args.overlayType}`)
    lines.push(`frames: ${args.live.frames}  selects: ${args.live.selects}`)
    lines.push(`pose: ${args.live.poseOk ? 'ok' : 'null'} emulated=${args.live.emulated}`)
    lines.push(`peak camera Y: ${args.peak.camY.toFixed(2)} m`)
    lines.push(`heading samples in session: ${args.peak.heading} (source ${args.live.headingSource ?? '—'})`)
    lines.push(`gps ticks in session: ${args.peak.gps} (last ±${args.live.gpsAcc?.toFixed(0) ?? '—'} m)`)
    if (args.live.gpsError) lines.push(`gps error: ${args.live.gpsError}`)
  }
  return lines.join('\n')
}
